import { AlertCodeCLT00035, ErrorCode, ReWriteCardParameter } from '@/types'
import { alertStore, dataStore, reWriteCardStore } from '@/utils/store-accessor'
import { AlertLevel, defaultAlert } from '@/api'
import { AlertFactory } from '@/factory'
import { GlobalWebSocketService } from '@/plugins/websocket'
import { GlobalModalAlertService } from '@/plugins/modal'
import { PAGE_LOADING_END, PAGE_LOADING_START } from '@/config/event'
import { EventBus } from '@/plugins/event_bus'

// WebSocket接続のタイムアウト設定時間
const CONNECT_TIME_OUT_MS: number = 500

// 本serviceの引数の扱いだが数が多くなる傾向がある為、プロパティできちんと名称をつけてオブジェクトで管理する
// 引数設定の順序ミスを無くす為の対応でしか無い為、深い意味はない
export class ReWriteCardService {
  public read(obj?: {
    fetchingUser?: Function
    readed?: Function
    readCompleted?: Function
    unConnectedReaderWriter?: Function
  }): void {
    this.readInterface({ ...obj, isReadOnlyRegisteredUser: false })
  }

  public readRegisteredUser(obj?: {
    fetchingUser?: Function
    readed?: Function
    readCompleted?: Function
    unConnectedReaderWriter?: Function
  }): void {
    this.readInterface({
      ...obj,
      isReadOnlyRegisteredUser: true
    })
  }

  private readInterface(obj?: {
    fetchingUser?: Function
    readed?: Function
    readCompleted?: Function
    unConnectedReaderWriter?: Function
    isReadOnlyRegisteredUser?: boolean
  }): void {
    if (!dataStore.enabledReWriteCard) {
      if (obj?.unConnectedReaderWriter) {
        obj.unConnectedReaderWriter()
      }
      return
    }

    // 最初にwebSocketの疎通チェックをしてから各種条件チェックをする必要がある
    GlobalWebSocketService.connect(() => {
      if (
        !GlobalWebSocketService.isOpen() ||
        !GlobalWebSocketService.reWriteCardEnabled
      ) {
        if (obj?.unConnectedReaderWriter) {
          obj.unConnectedReaderWriter()
        }
        return
      }

      // ここの処理に限り、リーダライタ装置の電源がoffでもでも処理を渡せるように修正
      if (
        reWriteCardStore.processing ||
        reWriteCardStore.loaded ||
        reWriteCardStore.readPending
      ) {
        return
      }

      reWriteCardStore.startProcess()

      GlobalWebSocketService.sendReWriteCardRead(
        (isConnect: boolean, parameter?: ReWriteCardParameter) => {
          const initialize = reWriteCardStore.endProcess
          const complete = () => {
            reWriteCardStore.startReadPending()
            reWriteCardStore.endProcess()
            reWriteCardStore.powerOn()

            if (obj?.readed) {
              obj.readed()
            }
          }

          if (!isConnect) {
            initialize()
            if (obj?.unConnectedReaderWriter) {
              obj.unConnectedReaderWriter()
            }
            return
          }

          if (!parameter) {
            initialize()
            return
          }

          if (
            !parameter.result &&
            parameter.code === ErrorCode.ReWriteCard.RewriteCardClosed
          ) {
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            GlobalModalAlertService.open({
              onOkClick: () => {
                initialize()
                reWriteCardStore.powerOff()

                if (obj?.unConnectedReaderWriter) {
                  obj.unConnectedReaderWriter()
                }
              }
            })
            return
          }

          // 動作を確認した限りでは読込みに失敗したとしてもそのエラーが返ってくるのは読込み完了のレスポンスの方なのでアラート表示処理は存在意義は無いと考えるが一応設定しておく
          if (!parameter.result) {
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            GlobalModalAlertService.open({
              onOkClick: initialize
            })
            return
          }

          complete()
        },
        (isConnect: boolean, parameter?: ReWriteCardParameter) => {
          // 他の構文と違い、processingを設定できない都合がある。
          // その為、initializeの処理はアラートを表示した時点で実行するように修正する必要がある

          const initialize = () => {
            reWriteCardStore.endReadPending()
            reWriteCardStore.endProcess()
          }
          const complete = () => {
            reWriteCardStore.endReadPending()
            reWriteCardStore.endProcess()

            if (obj?.readCompleted) {
              obj.readCompleted()
            }
          }

          if (!isConnect) {
            initialize()
            if (obj?.unConnectedReaderWriter) {
              obj.unConnectedReaderWriter()
            }
          }

          if (!parameter) {
            initialize()
            return
          }

          if (
            !parameter.result &&
            parameter.code === ErrorCode.ReWriteCard.RewriteCardClosed
          ) {
            initialize()
            // リクエストの度にアラートが逐一表示されないように同エラーは一度だけアラートを表示とする
            if (reWriteCardStore.power) {
              alertStore.setItem(
                Object.assign(defaultAlert(), {
                  code: parameter.code,
                  level: AlertLevel.Error,
                  message: parameter.message
                })
              )
              GlobalModalAlertService.open({
                onOkClick: () => {
                  reWriteCardStore.powerOff()

                  if (obj?.unConnectedReaderWriter) {
                    obj.unConnectedReaderWriter()
                  }
                }
              })
            } else if (obj?.unConnectedReaderWriter) {
              obj.unConnectedReaderWriter()
            }
            return
          }

          // 汎用性の高いエラーコードらしく、複数のエラーでコードが使い回されていることを確認済み
          // しかし、読込みにおいては「利用者カード読込に失敗しました。」でしか利用を確認が出来ないので固有のエラーコードとして処理する
          if (
            !parameter.result &&
            parameter.code === ErrorCode.ReWriteCard.RewriteCardRequestFailed
          ) {
            initialize()
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            GlobalModalAlertService.open({
              onOkClick: () => this.readInterface(obj)
            })
            return
          }

          // 利用者番号の読み取り成功を知らせるレスポンスだと確定した時点でデータをロードする
          reWriteCardStore.loadUserNo(parameter?.userNo ?? '')

          if (
            parameter.code === ErrorCode.ReWriteCard.ReWriteCardUserNoNotWritten
          ) {
            if (obj?.isReadOnlyRegisteredUser) {
              // 利用者番号が設定されていない利用者は現時点ではあり得ないので検索自体行わない
              alertStore.setItem(
                Object.assign(defaultAlert(), {
                  code: parameter.code,
                  level: AlertLevel.Error,
                  message: parameter.message
                })
              )
              GlobalModalAlertService.open({
                onOkClick: () => {
                  initialize()
                  this.eject({
                    ejected: () => this.readInterface(obj)
                  })
                }
              })
              return
            }

            complete()
            return
          }

          if (obj?.fetchingUser) {
            obj.fetchingUser(
              parameter.userNo,
              () => {
                initialize()
                this.eject({
                  ejected: () => this.readInterface(obj)
                })
              },
              complete
            )
            return
          }

          complete()
        }
      )
    })

    // 独自のタイムアウト設定
    // webSocket接続確認をする際、closeイベントが発生するまでにどうしても3秒以上かかる為、一定の時間を超える場合はclose()を強制的に実行させて処理を中断させる
    GlobalWebSocketService.disconnectTimeout(CONNECT_TIME_OUT_MS)
  }

  public readCancel(obj?: {
    readCanceled?: Function
    unConnectedReaderWriter?: Function
  }): void {
    if (!dataStore.enabledReWriteCard) {
      if (obj?.unConnectedReaderWriter) {
        obj.unConnectedReaderWriter()
      }
      return
    }

    // 最初にwebSocketの疎通チェックをしてから各種条件チェックをする必要がある
    GlobalWebSocketService.connect(() => {
      if (
        !GlobalWebSocketService.isOpen() ||
        !GlobalWebSocketService.reWriteCardEnabled
      ) {
        if (obj?.unConnectedReaderWriter) {
          obj.unConnectedReaderWriter()
        }
        return
      }

      if (
        reWriteCardStore.processing ||
        reWriteCardStore.loaded ||
        !reWriteCardStore.readPending
      ) {
        return
      }

      const initialize = reWriteCardStore.endProcess
      const complete = () => {
        reWriteCardStore.endReadPending()
        reWriteCardStore.endProcess()

        if (obj?.readCanceled) {
          obj.readCanceled()
        }
      }

      reWriteCardStore.startProcess()

      GlobalWebSocketService.sendReWriteCardReadCancel(
        (isConnect: boolean, parameter?: ReWriteCardParameter) => {
          if (!isConnect || !parameter) {
            initialize()

            if (obj?.unConnectedReaderWriter) {
              obj.unConnectedReaderWriter()
            }
            return
          }

          if (
            !parameter.result &&
            parameter.code === ErrorCode.ReWriteCard.RewriteCardClosed
          ) {
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            GlobalModalAlertService.open({
              onOkClick: () => {
                initialize()
                reWriteCardStore.powerOff()

                if (obj?.unConnectedReaderWriter) {
                  obj.unConnectedReaderWriter()
                }
              }
            })
            return
          }

          if (!parameter.result) {
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            GlobalModalAlertService.open({
              onOkClick: initialize
            })
            return
          }

          complete()
        }
      )
    })

    // 独自のタイムアウト設定
    // webSocket接続確認をする際、closeイベントが発生するまでにどうしても3秒以上かかる為、一定の時間を超える場合はclose()を強制的に実行させて処理を中断させる
    GlobalWebSocketService.disconnectTimeout(CONNECT_TIME_OUT_MS)
  }

  public write(obj: {
    parameter: {
      userNo: string
      branchName: string
      collections: {
        title: string
        dueDate: string
      }[]
    }
    wrote?: Function
    unConnectedReaderWriter?: Function
    notProcessed?: Function
  }): void {
    if (!dataStore.enabledReWriteCard) {
      if (obj?.unConnectedReaderWriter) {
        obj.unConnectedReaderWriter()
      }
      return
    }

    // 最初にwebSocketの疎通チェックをしてから各種条件チェックをする必要がある
    GlobalWebSocketService.connect(() => {
      if (
        !GlobalWebSocketService.isOpen() ||
        !GlobalWebSocketService.reWriteCardEnabled
      ) {
        if (obj?.unConnectedReaderWriter) {
          obj.unConnectedReaderWriter()
        }
        return
      }

      if (
        reWriteCardStore.processing ||
        !reWriteCardStore.loaded ||
        reWriteCardStore.readPending
      ) {
        return
      }

      const writeReWriteCard = () => {
        const initialize = reWriteCardStore.endProcess
        const complete = () => {
          reWriteCardStore.unLoadUserNo()
          reWriteCardStore.endProcess()

          if (obj.wrote) {
            obj.wrote()
          }
        }

        reWriteCardStore.startProcess()

        GlobalWebSocketService.sendReWriteCardWrite(
          {
            ...obj.parameter,
            writeFlag: '2'
          },
          (isConnect: boolean, parameter?: ReWriteCardParameter) => {
            if (!isConnect) {
              initialize()
              if (obj?.unConnectedReaderWriter) {
                obj.unConnectedReaderWriter()
              }
            }

            if (!parameter) {
              initialize()
              return
            }

            if (
              !parameter.result &&
              parameter.code === ErrorCode.ReWriteCard.RewriteCardClosed
            ) {
              alertStore.setItem(
                Object.assign(defaultAlert(), {
                  code: parameter.code,
                  level: AlertLevel.Error,
                  message: parameter.message
                })
              )
              GlobalModalAlertService.open({
                onOkClick: () => {
                  initialize()
                  reWriteCardStore.powerOff()

                  if (obj?.unConnectedReaderWriter) {
                    obj.unConnectedReaderWriter()
                  }
                }
              })
              return
            }

            if (!parameter.result) {
              alertStore.setItem(
                Object.assign(defaultAlert(), {
                  code: parameter.code,
                  level: AlertLevel.Error,
                  message: parameter.message
                })
              )
              GlobalModalAlertService.open({
                onOkClick: initialize
              })
            }
          },
          (isConnect: boolean, parameter?: ReWriteCardParameter) => {
            if (!isConnect) {
              initialize()
              if (obj?.unConnectedReaderWriter) {
                obj.unConnectedReaderWriter()
              }
            }

            if (!parameter) {
              initialize()
              return
            }

            if (
              !parameter.result &&
              parameter.code === ErrorCode.ReWriteCard.RewriteCardClosed
            ) {
              alertStore.setItem(
                Object.assign(defaultAlert(), {
                  code: parameter.code,
                  level: AlertLevel.Error,
                  message: parameter.message
                })
              )
              GlobalModalAlertService.open({
                onOkClick: () => {
                  initialize()
                  reWriteCardStore.powerOff()

                  if (obj?.unConnectedReaderWriter) {
                    obj.unConnectedReaderWriter()
                  }
                }
              })
              return
            }

            if (!parameter.result) {
              alertStore.setItem(
                Object.assign(defaultAlert(), {
                  code: parameter.code,
                  level: AlertLevel.Error,
                  message: parameter.message
                })
              )
              GlobalModalAlertService.open({
                onOkClick: initialize
              })
              return
            }

            complete()
          }
        )
      }

      if (obj.parameter.userNo !== reWriteCardStore.loadedUserNo) {
        alertStore.setItem(AlertFactory.create(AlertCodeCLT00035))
        GlobalModalAlertService.open({
          onOkClick: writeReWriteCard,
          onCancelClick: () => {
            if (obj?.notProcessed) {
              obj.notProcessed()
            }
          }
        })
        return
      }

      writeReWriteCard()
    })

    // 独自のタイムアウト設定
    // webSocket接続確認をする際、closeイベントが発生するまでにどうしても3秒以上かかる為、一定の時間を超える場合はclose()を強制的に実行させて処理を中断させる
    GlobalWebSocketService.disconnectTimeout(CONNECT_TIME_OUT_MS)
  }

  public register(obj: {
    parameter: {
      userNo: string
      branchName: string
      collections: {
        title: string
        dueDate: string
      }[]
    }
    isEject: boolean
    registered?: Function
    unConnectedReaderWriter?: Function
  }): void {
    if (!dataStore.enabledReWriteCard) {
      if (obj?.unConnectedReaderWriter) {
        obj.unConnectedReaderWriter()
      }
      return
    }

    // 最初にwebSocketの疎通チェックをしてから各種条件チェックをする必要がある
    GlobalWebSocketService.connect(() => {
      if (
        !GlobalWebSocketService.isOpen() ||
        !GlobalWebSocketService.reWriteCardEnabled
      ) {
        if (obj?.unConnectedReaderWriter) {
          obj.unConnectedReaderWriter()
        }
        return
      }

      if (
        reWriteCardStore.processing ||
        !reWriteCardStore.loaded ||
        reWriteCardStore.readPending
      ) {
        return
      }
      const openAlert = (callback: {
        okCallback?: any
        cancelCallback?: any
      }) => {
        if (!obj.isEject) {
          EventBus.emit(PAGE_LOADING_END)
        }
        GlobalModalAlertService.open({
          onOkClick: callback?.okCallback,
          onCancelClick: callback?.cancelCallback
        })
      }
      const initialize = () => {
        if (!obj.isEject) {
          EventBus.emit(PAGE_LOADING_END)
        }
        reWriteCardStore.endProcess()
      }
      const complete = () => {
        if (obj.isEject) {
          reWriteCardStore.unLoadUserNo()
        } else {
          EventBus.emit(PAGE_LOADING_END)
          reWriteCardStore.loadUserNo(obj.parameter.userNo)
        }
        reWriteCardStore.endProcess()

        if (obj.registered !== undefined) {
          obj.registered()
        }
      }

      reWriteCardStore.startProcess()

      if (!obj.isEject) {
        EventBus.emit(PAGE_LOADING_START)
      }

      GlobalWebSocketService.sendReWriteCardWrite(
        {
          ...obj.parameter,
          writeFlag: obj.isEject ? '1' : '3'
        },
        (isConnect: boolean, parameter?: ReWriteCardParameter) => {
          if (!isConnect) {
            initialize()
            if (obj?.unConnectedReaderWriter) {
              obj.unConnectedReaderWriter()
            }
          }

          if (!parameter) {
            initialize()
            return
          }

          if (
            !parameter.result &&
            parameter.code === ErrorCode.ReWriteCard.RewriteCardClosed
          ) {
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            openAlert({
              okCallback: () => {
                initialize()
                reWriteCardStore.powerOff()

                if (obj?.unConnectedReaderWriter) {
                  obj.unConnectedReaderWriter()
                }
              }
            })
            return
          }

          if (!parameter.result) {
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            openAlert({
              okCallback: initialize
            })
          }
        },
        (isConnect: boolean, parameter?: ReWriteCardParameter) => {
          if (!isConnect) {
            initialize()
            if (obj?.unConnectedReaderWriter) {
              obj.unConnectedReaderWriter()
            }
          }

          if (!parameter) {
            initialize()
            return
          }

          if (
            !parameter.result &&
            parameter.code === ErrorCode.ReWriteCard.RewriteCardClosed
          ) {
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            openAlert({
              okCallback: () => {
                initialize()
                reWriteCardStore.powerOff()

                if (obj?.unConnectedReaderWriter) {
                  obj.unConnectedReaderWriter()
                }
              }
            })
            return
          }

          if (!parameter.result) {
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            openAlert({
              okCallback: initialize
            })
            return
          }

          complete()
        }
      )
    })

    // 独自のタイムアウト設定
    // webSocket接続確認をする際、closeイベントが発生するまでにどうしても3秒以上かかる為、一定の時間を超える場合はclose()を強制的に実行させて処理を中断させる
    GlobalWebSocketService.disconnectTimeout(CONNECT_TIME_OUT_MS)
  }

  public eject(obj?: {
    ejected?: Function
    unConnectedReaderWriter?: Function
  }): void {
    if (!dataStore.enabledReWriteCard) {
      if (obj?.unConnectedReaderWriter) {
        obj.unConnectedReaderWriter()
      }
      return
    }

    // 最初にwebSocketの疎通チェックをしてから各種条件チェックをする必要がある
    GlobalWebSocketService.connect(() => {
      if (
        !GlobalWebSocketService.isOpen() ||
        !GlobalWebSocketService.reWriteCardEnabled
      ) {
        if (obj?.unConnectedReaderWriter) {
          obj.unConnectedReaderWriter()
        }
        return
      }

      if (
        reWriteCardStore.processing ||
        !reWriteCardStore.loaded ||
        reWriteCardStore.readPending
      ) {
        return
      }

      const initialize = reWriteCardStore.endProcess
      const complete = () => {
        reWriteCardStore.unLoadUserNo()
        reWriteCardStore.endProcess()

        if (obj?.ejected) {
          // 排出後の処理はカードが完全に排出されるまでの時間があるので処理を少しだけ遅らせるように修正する
          setTimeout(obj.ejected, 500)
        }
      }

      reWriteCardStore.startProcess()

      GlobalWebSocketService.sendReWriteCardEject(
        (isConnect: boolean, parameter?: ReWriteCardParameter) => {
          if (!isConnect) {
            initialize()
            if (obj?.unConnectedReaderWriter) {
              obj.unConnectedReaderWriter()
            }
          }

          if (!parameter) {
            initialize()
            return
          }

          if (
            !parameter.result &&
            parameter.code === ErrorCode.ReWriteCard.RewriteCardClosed
          ) {
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            GlobalModalAlertService.open({
              onOkClick: () => {
                initialize()
                reWriteCardStore.powerOff()

                if (obj?.unConnectedReaderWriter) {
                  obj.unConnectedReaderWriter()
                }
              }
            })
            return
          }

          if (!parameter.result) {
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            GlobalModalAlertService.open({
              onOkClick: initialize
            })
            return
          }

          complete()
        }
      )
    })

    // 独自のタイムアウト設定
    // webSocket接続確認をする際、closeイベントが発生するまでにどうしても3秒以上かかる為、一定の時間を超える場合はclose()を強制的に実行させて処理を中断させる
    GlobalWebSocketService.disconnectTimeout(CONNECT_TIME_OUT_MS)
  }
}
